import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { useGame } from "./hooks/useGame";
import { useDemoGame } from "./hooks/useDemoGame";
import { useAuth } from "./contexts/AppKitAuthContext";
import { useAlert } from "./hooks/useAlert";
import { useBNBPrice } from "./hooks/useBNBPrice";
import { useLeaderboard } from "./hooks/useLeaderboard";
import { RowConfig } from "./types";
import Verifier from "./components/Verifier";
import GameHashDisplay from "./components/GameHashDisplay";
import AlertPopup from "./components/AlertPopup";
import TopNavigation from "./components/TopNavigation";
import HowToPlayPopup from "./components/HowToPlayPopup";
import EarningsPopup, { EarningsData } from "./components/EarningsPopup";
import ShareButton from "./components/ShareButton";
import GameCreationPopup from "./components/GameCreationPopup";
import EventBanner from "./components/EventBanner";
import FloatingDrawButton from "./components/FloatingDrawButton";
import WelcomeAirdropNotification from "./components/WelcomeAirdropNotification";
import WeeklyLeaderboard from "./components/WeeklyLeaderboard";
import BottomNavigation from "./components/BottomNavigation";
import { CashOutModal } from "./components/CashOutModal";
import { StreakReminderModal } from "./components/StreakReminderModal";
import RecoverStreakModal from "./components/RecoverStreakModal";
import NotificationPopup from "./components/NotificationPopup";
import { useEventBanner } from "./hooks/useEventBanner";
import { useNotifications } from "./hooks/useNotifications";
import "./App.css";
import gameBoardStyles from "./styles/GameBoard.module.css";
import {
  calculatePotentialPayout,
  calculateMultiplier,
  formatMultiplier,
  formatPayoutAmount,
  formatDXFBalance,
  calculatePotentialPayoutUSD,
  getNextRowEarningsUSD,
} from "./utils/gameCalculations";
import { formatUSD } from "./utils/priceAPI";
import { appKitBlockchainService as blockchainService } from "./services/appkitBlockchain";
import { getReferrerCodeFromURL, storeReferrerCode } from "./utils/referral";
import { getStoredBetPreference, storeBetPreference } from "./utils/betPreferences";
import { shouldShowStreakReminder, setReminderTime, shouldShowRecoveryReminderModal, setRecoveryReminderTime } from "./utils/streakReminder";
import { GameUtilities } from "./types";
import { ApiError, ApiService } from "./services/api";
import { SourceTracker } from "./utils/sourceTracking";
import { initDebugConsole } from "./services/debug";

const DEFAULT_ROW_CONFIGS: RowConfig[] = [
  7, 6, 5, 4, 3, 4, 5, 6, 7, 6, 5, 4, 3, 4, 5, 6, 7, 6, 5, 4, 3, 4, 5, 6, 7,
].map((tiles) => ({ tiles }));

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, walletAddress, login, walletState, updateBalance, isInitializing } = useAuth();
  const { gameState, createGame, makeMove, skipRow, cashOut, resetGame, checkForActiveGame } = useGame();
  const { demoGameState, startDemoGame, makeDemoMove, skipDemoRow, resetDemoGame, cashOutDemo } = useDemoGame();
  const { alert, showError, showSuccess, showWarning, hideAlert } = useAlert();
  const { price: bnbPrice, loading: priceLoading } = useBNBPrice();
  
  // Global leaderboard hook for streak data (always active for streak reminders)
  const { userStreak, refetch: refetchLeaderBoard } = useLeaderboard(true);
  
  // Notifications hook
  const { notifications, hasNotifications, checkForNotifications, clearNotifications, removeNotification } = useNotifications();

  const [betAmount, setBetAmount] = useState(() => getStoredBetPreference());

  // Predefined bet amount options
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showResultPanel, setShowResultPanel] = useState(false);
  const [isResultPanelCollapsed, setIsResultPanelCollapsed] = useState(false);
  const [showVerifier, setShowVerifier] = useState(false);
  const [verifierParams, setVerifierParams] = useState<{
    gameConfig?: string;
    seed?: string;
    seedHash?: string;
  }>({});
  const [shakingTile, setShakingTile] = useState<{
    row: number;
    tile: number;
  } | null>(null);
  const [currentRewardRatio, setCurrentRewardRatio] = useState<string | null>(null);
  const [showHowToPlayPopup, setShowHowToPlayPopup] = useState(false);
  const [earningsData, setEarningsData] = useState<EarningsData | null>(null);
  const [completedRow, setCompletedRow] = useState<number | null>(null);
  const [showGameCreationPopup, setShowGameCreationPopup] = useState(false);
  const [showCashOutModal, setShowCashOutModal] = useState(false);
  const [showStreakReminderModal, setShowStreakReminderModal] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [mobileTab, setMobileTab] = useState<"play" | "leaderboard">("play");
  const gameScrollRef = useRef<HTMLDivElement>(null);
  const previousRowRef = useRef<number>(-1);
  const gameInitializedRef = useRef<string | null>(null); // Track game ID to detect new games

  // Handle routing for sub-page URLs
  useEffect(() => {
    const pathname = location.pathname;

    if (pathname === "/verifier") {
      // Extract query parameters for verifier
      const urlParams = new URLSearchParams(location.search);
      const gameConfig = urlParams.get("gameConfig") || "";
      const seed = urlParams.get("seed") || "";
      const seedHash = urlParams.get("seedHash") || "";

      setVerifierParams({ gameConfig, seed, seedHash });
      setShowVerifier(true);
    } else if (pathname === "/faq") {
      // Open FAQ popup and navigate back to home
      setShowHowToPlayPopup(true);
      navigate("/", { replace: true });
    } else if (pathname === "/docs") {
      // Redirect to Notion docs
      window.location.href = "https://deathx-fun.notion.site/DeathX-fun-Docs-24e1b048608b80f69c33d28127a39c53";
      return;
    }
  }, [location, navigate]);

  // Use demo game state when in demo mode, otherwise use real game state
  const currentGameState = isDemoMode ? demoGameState : gameState;
  const currentGame = currentGameState.game;

  // Event banner for blockchain events (only when not actively playing)
  const shouldShowEventBanner = useMemo(() => {
    return (isDemoMode || !currentGame || currentGame.status !== "active") && mobileTab !== "leaderboard";
  }, [isDemoMode, currentGame, mobileTab]);

  const { currentEvent, closeCurrentEvent } = useEventBanner({
    enabled: shouldShowEventBanner,
  });

  // Helper functions for utilities
  const canUseRowSkip = useCallback(() => {
    return (
      currentGame?.utilities?.rowSkip?.purchased &&
      currentGame?.utilities?.rowSkip?.usedOnRow === undefined &&
      currentGame?.status === "active"
    );
  }, [currentGame?.utilities?.rowSkip, currentGame?.status]);

  const getDXFMiningBoostInfo = useCallback(() => {
    if (!currentGame?.utilities?.dxfMiningBoost?.purchased) {
      return null;
    }

    const boostCount = currentGame.utilities.dxfMiningBoost.count;
    const boostPercentage = boostCount * 10;

    return {
      count: boostCount,
      percentage: boostPercentage,
      multiplier: 1 + boostPercentage / 100, // 1.1x, 1.2x, etc.
    };
  }, [currentGame?.utilities?.dxfMiningBoost]);

  const handleRowSkip = useCallback(async () => {
    if (!currentGame || currentGameState.loading || !canUseRowSkip()) return;

    try {
      if (isDemoMode) {
        skipDemoRow();
        // showSuccess("Row Skipped!", `Successfully skipped Row ${currentGame.currentRow + 1} in demo mode!`);
      } else {
        await skipRow(currentGame.gameId);
        // showSuccess("Row Skipped!", `Successfully skipped Row ${currentGame.currentRow + 1}!`);
      }
    } catch (error) {
      showError("Row Skip Failed", `Failed to skip row: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }, [currentGame, currentGameState.loading, canUseRowSkip, skipRow, skipDemoRow, isDemoMode, showError]);

  // Fetch current reward ratio from contract
  const fetchCurrentRewardRatio = useCallback(async () => {
    try {
      if (blockchainService.isConnected() && !isDemoMode) {
        const currentRatio = await blockchainService.getCurrentRewardRatio();
        setCurrentRewardRatio(currentRatio);
      }
    } catch (error) {
      console.error("Failed to fetch current reward ratio:", error);
      // Fallback to default value
      setCurrentRewardRatio("10000");
    }
  }, [isDemoMode]);

  // Save bet preference when user changes selection
  useEffect(() => {
    storeBetPreference(betAmount);
  }, [betAmount]);

  // Check URL parameters for verifier mode and referrer code
  useEffect(() => {
    // Initialize debug console for mobile debugging in dev mode
    initDebugConsole();

    const handleError = (event: ErrorEvent) => {
      console.error("Global error detected:", {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error,
        stack: event.error?.stack,
        timestamp: new Date().toISOString(),
      });

      // Check for AppKit-specific errors
      if (
        event.message &&
        (event.message.includes("getProvider") ||
          event.message.includes("getDefaultChain") ||
          event.message.includes("this.getProvider(s)"))
      ) {
        console.error("AppKit provider error in global handler:", event.message);
      }
    };

    window.addEventListener("error", handleError);

    // Initialize source tracking for marketing campaigns
    const sourceData = SourceTracker.initialize();
    if (sourceData) {
      console.log("Source tracking initialized:", sourceData);
    }

    // Handle referrer code
    const referrerCode = getReferrerCodeFromURL();
    if (referrerCode !== null) {
      console.log("Referrer code detected:", referrerCode);
      storeReferrerCode(referrerCode as string);
    }

    // Cleanup function
    return () => {
      window.removeEventListener("error", handleError);
    };
  }, []);

  // Function to scroll to bottom (Row 1)
  const scrollToBottom = useCallback(() => {
    if (gameScrollRef.current) {
      gameScrollRef.current.scrollTop = gameScrollRef.current.scrollHeight;
    }
  }, []);

  const isMobile = window.innerWidth <= 768;
  // Function to scroll to a specific row (center it in viewport)
  const scrollToRow = useCallback(
    (rowIndex: number) => {
      if (gameScrollRef.current && currentGame) {
        const container = gameScrollRef.current;
        let scrollPosition;

        if (isMobile) {
          // Desktop: Center the current row properly
          const estimatedRowHeight = 55;
          const titleSectionHeight = 0;
          const containerHeight = container.clientHeight;

          // Calculate the position of the current row in the DOM
          // Rows are displayed in reverse: Row 25 at top, Row 1 near bottom
          const totalRows = currentGame.rowConfigs.length;
          const reversedRowIndex = totalRows - 1 - rowIndex;
          const targetRowPosition = titleSectionHeight + reversedRowIndex * estimatedRowHeight;

          // Center the row: scroll so row appears in middle of viewport
          scrollPosition = targetRowPosition - containerHeight / 2 + estimatedRowHeight / 2;
        } else {
          // Desktop: Center the current row properly
          const estimatedRowHeight = 80;
          const titleSectionHeight = 160;
          const containerHeight = container.clientHeight;

          // Calculate the position of the current row in the DOM
          // Rows are displayed in reverse: Row 25 at top, Row 1 near bottom
          const totalRows = currentGame.rowConfigs.length;
          const reversedRowIndex = totalRows - 1 - rowIndex;
          const targetRowPosition = titleSectionHeight + reversedRowIndex * estimatedRowHeight;

          // Center the row: scroll so row appears in middle of viewport
          scrollPosition = targetRowPosition - containerHeight / 2 + estimatedRowHeight / 2;
        }

        const targetScroll = Math.max(0, scrollPosition);

        // Try smooth scroll first, fallback to instant if needed
        try {
          container.scrollTo({
            top: targetScroll,
            behavior: "smooth",
          });

        } catch (error) {
          // Fallback for older browsers
          container.scrollTop = targetScroll;
        }
      }
    },
    [currentGame, isMobile]
  );

  const handleOpenGameCreation = useCallback(() => {
    if (!isAuthenticated || !walletAddress || !walletState.address) {
      showError("Wallet Required", "Please login with your wallet first to start playing.");
      return;
    }
    setShowGameCreationPopup(true);
  }, [isAuthenticated, walletAddress, walletState.address, showError]);

  const handleCreateGameWithUtilities = useCallback(
    async (selectedBetAmount: string, utilities: GameUtilities) => {
      if (!isAuthenticated || !walletAddress || !walletState.address) {
        showError("Wallet Required", "Please login with your wallet first to start playing.");
        return;
      }

      const betInWei = ethers.parseEther(selectedBetAmount).toString();

      try {
        // Reset result panel state when starting a new game
        setIsResultPanelCollapsed(false);
        // Reset previous row reference for auto-scroll
        previousRowRef.current = -1;
        // Reset game initialization tracking
        gameInitializedRef.current = null;

        // Update stored bet preference
        storeBetPreference(selectedBetAmount);
        setBetAmount(selectedBetAmount);

        // Track game creation event with source data
        SourceTracker.trackSourceEvent("game_created");

        // Pass utilities to createGame function
        await createGame(betInWei, walletAddress, utilities);

        // Close the popup
        setShowGameCreationPopup(false);

        // Scroll to bottom (Row 1) after game creation
        setTimeout(scrollToBottom, 100);
        setTimeout(updateBalance, 1000); // Update balance after game creation
      } catch (error) {
        if (error instanceof Error && error.message.includes("already have an active game")) {
          showWarning(
            "Active Game Found",
            "You already have an active game. Please complete or cash out your current game before starting a new one."
          );
          // Try to load the active game
          await checkForActiveGame();
          setShowGameCreationPopup(false);
        } else {
          let errorMessage = "Unknown error";
          if (error instanceof ApiError) {
            errorMessage = error.message; // This will now show the detailed backend error
          } else if (error instanceof Error) {
            errorMessage = error.message;
          }
          showError("Game Creation Failed", `${errorMessage}`);
        }
      }
    },
    [
      isAuthenticated,
      walletAddress,
      walletState,
      createGame,
      updateBalance,
      checkForActiveGame,
      scrollToBottom,
      showError,
      showWarning,
    ]
  );

  const handleStartDemoGame = useCallback(async () => {
    setIsDemoMode(true);
    // Reset result panel state when starting a new demo game
    setIsResultPanelCollapsed(false);
    // Reset previous row reference for auto-scroll
    previousRowRef.current = -1;
    // Reset game initialization tracking
    gameInitializedRef.current = null;
    await startDemoGame(DEFAULT_ROW_CONFIGS);
    setTimeout(scrollToBottom, 100);
  }, [startDemoGame, scrollToBottom]);

  const handleBackToRealGame = useCallback(() => {
    resetDemoGame();
    setIsDemoMode(false);
  }, [resetDemoGame]);

  const handlePlayForReal = useCallback(async () => {
    try {
      // Reset demo game first
      resetDemoGame();
      setIsDemoMode(false);

      // Directly trigger wallet connection and login
      await login();
    } catch (error) {
      console.error("Failed to connect wallet:", error);
    }
  }, [resetDemoGame, login]);

  const handleTileClick = useCallback(
    async (rowIndex: number, tileIndex: number) => {
      if (!currentGame || currentGameState.loading) return;

      if (currentGame.status !== "active" || rowIndex !== currentGame.currentRow) {
        return;
      }

      // Start shaking animation immediately for visual feedback
      setShakingTile({ row: rowIndex, tile: tileIndex });

      // Clear shaking animation after animation duration
      setTimeout(() => {
        setShakingTile(null);
      }, 400);

      if (isDemoMode) {
        // Handle demo game move
        makeDemoMove(tileIndex);
      } else {
        // Handle real game move
        try {
          await makeMove(currentGame.gameId, tileIndex);
        } catch (error) {
          showError("Move Failed", `Failed to make move: ${error instanceof Error ? error.message : "Unknown error"}`);
        }
      }
    },
    [currentGame, currentGameState.loading, isDemoMode, makeDemoMove, makeMove, showError]
  );

  const handleCashOut = useCallback(async () => {
    if (!currentGame || currentGameState.loading) return;

    if (isDemoMode) {
      // Handle demo cash out
      cashOutDemo();
    } else {
      // Show modal for user to choose cash out option
      setShowCashOutModal(true);
    }
  }, [currentGame, currentGameState.loading, isDemoMode, cashOutDemo]);

  const handleJustCashOut = useCallback(async () => {
    if (!currentGame) return;
    setShowCashOutModal(false);

    try {
      const result = await cashOut(currentGame.gameId);
      console.log("cashout result", result);
      showSuccess("Cash Out Successful!", `Successfully cashed out ${ethers.formatEther(result.payoutAmount)} BNB!`);
      setTimeout(updateBalance, 1000);
    } catch (error) {
      showError("Cash Out Failed", `${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }, [currentGame, cashOut, updateBalance, showError, showSuccess]);

  const handleCashOutAndPlayAgain = useCallback(async () => {
    if (!currentGame) return;
    setShowCashOutModal(false);

    try {
      const result = await cashOut(currentGame.gameId, true); // true for createGameAgain
      console.log("cashout and reinvest result", result);
      showSuccess("Cash Out & Game Created!", `Successfully cashed out ${ethers.formatEther(result.payoutAmount)} BNB and created new game with free DXF Mining Boost!`);
      setTimeout(updateBalance, 1000);
    } catch (error) {
      showError("Cash Out Failed", `${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }, [currentGame, cashOut, updateBalance, showError, showSuccess]);

  const getTileStatus = (rowIndex: number, tileIndex: number): string => {
    if (!currentGame) return "hidden";

    // Check if this row was skipped
    const skippedMove = currentGame.moves.find((m) => m.row === rowIndex && m.result === "skipped");
    if (skippedMove) {
      return "skipped";
    }

    // Check if Phoenix Revival was used on this row
    const isRevivedRow = currentGame.utilities?.phoenixRevival?.usedOnRow === rowIndex;

    const move = currentGame.moves.find((m) => m.row === rowIndex && m.tileIndex === tileIndex);
    let status = "";

    if (move) {
      if (isRevivedRow && move.result === "revived") {
        // This is the tile that triggered Phoenix Revival
        status = "revived-death";
      } else {
        status = move.result === "death" ? "death" : "safe";
      }
    } else {
      // Show death tiles for completed rows (rows below current) OR after game ends
      const shouldRevealDeathTiles =
        (currentGame.status === "active" && rowIndex < currentGame.currentRow) || currentGame.status !== "active";

      if (shouldRevealDeathTiles && currentGame.deathTiles) {
        const deathTile = currentGame.deathTiles.find((dt) => dt.row === rowIndex);
        if (deathTile && deathTile.position === tileIndex) {
          status = "revealed-death";

          // Add near-miss effect to adjacent tiles if they were chosen by player
          const playerMove = currentGame.moves.find((m) => m.row === rowIndex);
          if (playerMove && Math.abs(playerMove.tileIndex - tileIndex) === 1) {
            // Player chose tile adjacent to death tile - this is a near miss!
            // We'll handle this in the tile that was actually chosen
          }
        }
      }

      if (!status) {
        if (rowIndex < currentGame.currentRow) status = "disabled";
        else if (rowIndex === currentGame.currentRow && currentGame.status === "active") status = "active";
        else status = "hidden";
      }
    }

    // Check for near-miss effect (player chose tile next to revealed death tile)
    if (move && move.result === "safe" && currentGame.deathTiles && rowIndex < currentGame.currentRow) {
      const deathTile = currentGame.deathTiles.find((dt) => dt.row === rowIndex);
      if (deathTile && Math.abs(deathTile.position - tileIndex) === 1) {
        status += " near-miss";
      }
    }

    // Always add shaking class if this tile is currently shaking, regardless of other status
    if (shakingTile && shakingTile.row === rowIndex && shakingTile.tile === tileIndex) {
      status += " shaking";
    }

    return status;
  };

  // Map tile status string to CSS module class names
  const getTileStatusClasses = (status: string): string => {
    const statusClasses: string[] = [];

    // Handle compound statuses (e.g., "safe near-miss", "death shaking")
    const statusParts = status.split(" ");

    statusParts.forEach((part) => {
      switch (part) {
        case "safe":
          statusClasses.push(gameBoardStyles.safe);
          break;
        case "death":
          statusClasses.push(gameBoardStyles.death);
          break;
        case "revived-death":
          statusClasses.push(gameBoardStyles.revivedDeath);
          break;
        case "revealed-death":
          // Use death styling for revealed death tiles
          statusClasses.push(gameBoardStyles.death);
          break;
        case "skipped":
          statusClasses.push(gameBoardStyles.skipped);
          break;
        case "disabled":
          statusClasses.push(gameBoardStyles.disabled);
          break;
        case "active":
          statusClasses.push(gameBoardStyles.active);
          break;
        case "hidden":
          // Hidden tiles don't need additional styling
          break;
        case "shaking":
          statusClasses.push(gameBoardStyles.shaking);
          break;
        case "near-miss":
          // Add near-miss class if it exists in CSS
          if (gameBoardStyles.nearMiss) {
            statusClasses.push(gameBoardStyles.nearMiss);
          }
          break;
        default:
          // For any unknown status, don't add a class
          break;
      }
    });

    return statusClasses.join(" ");
  };

  const calculateCurrentMultiplier = (): number => {
    if (!currentGame || currentGame.currentRow === 0) return 1.0;
    const rowConfigs = currentGame.rowConfigs || DEFAULT_ROW_CONFIGS;
    // Current multiplier is based on completed rows (currentRow - 1 if on active row, currentRow if completed)
    return calculateMultiplier(
      currentGame.currentRow,
      rowConfigs,
      currentGame.utilities?.rowSkip?.usedOnRow,
      currentGame.utilities?.phoenixRevival?.usedOnRow
    );
  };

  const formatPotentialPayout = (): string => {
    if (!currentGame) return "0";
    const multiplier = calculateCurrentMultiplier();
    const bet = ethers.formatEther(BigInt(currentGame.betAmount || "0"));
    const payout = isDemoMode ? 1 * multiplier : parseFloat(bet) * multiplier;

    return formatPayoutAmount(payout);
    // // For completed games, use standard formatting
    // if (priceLoading) {
    //   return formatPayoutAmount(payout);
    // }
    // return formatPayoutWithUSD(payout, bnbPrice);
  };

  const getRowMultiplierDisplay = (rowIndex: number): string => {
    const multiplier = calculateMultiplier(
      rowIndex + 1,
      currentGame?.rowConfigs || DEFAULT_ROW_CONFIGS,
      currentGame?.utilities?.rowSkip?.usedOnRow,
      currentGame?.utilities?.phoenixRevival?.usedOnRow
    );
    return formatMultiplier(multiplier);
  };

  const getRowUSDPreview = (rowIndex: number): string | null => {
    if (!currentGame || priceLoading) {
      return null;
    }

    // Show USD earnings for next row if it's the current active row
    if (rowIndex === currentGame.currentRow && currentGame.status === "active") {
      // For demo mode, use 1 BNB as the bet amount
      const bet = isDemoMode ? "1" : ethers.formatEther(BigInt(currentGame.betAmount || "0"));
      const nextRowEarnings = getNextRowEarningsUSD(bet, currentGame.currentRow, currentGame.rowConfigs, bnbPrice);

      if (nextRowEarnings > 0) {
        return `+${formatUSD(nextRowEarnings)}`;
      }
    }

    return null;
  };

  // Check for active game when authenticated
  useEffect(() => {
    if (isAuthenticated && walletState.address && !gameState.game) {
      checkForActiveGame();
    }
  }, [isAuthenticated, walletState.address, gameState.game, checkForActiveGame]);

  // Check for notifications when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && walletState.address) {
      checkForNotifications();
    }
  }, [isAuthenticated, walletState.address, checkForNotifications]);

  // Fetch current reward ratio when user is authenticated and connected
  useEffect(() => {
    if (isAuthenticated && blockchainService.isConnected() && !isDemoMode) {
      fetchCurrentRewardRatio();
    }
  }, [isAuthenticated, isDemoMode, fetchCurrentRewardRatio]);

  // Handle scrolling when game loads or changes (only on initialization)
  useEffect(() => {
    if (currentGame && !currentGameState.loading) {
      const gameId = currentGame.gameId;

      // Only scroll if this is a new/different game (initialization)
      if (gameInitializedRef.current !== gameId) {
        gameInitializedRef.current = gameId;

        // For new games (at Row 0), scroll to bottom to show Row 1
        if (currentGame.currentRow === 0 && currentGame.moves.length === 0) {
          setTimeout(scrollToBottom, 100);
        }
        // For resumed games or games in progress, center the current row
        else if (currentGame.currentRow > 0) {
          setTimeout(() => {
            if (currentGame) {
              scrollToRow(currentGame.currentRow);
            }
          }, 200);
        }
      }
    }
    // For authenticated users with no game (start new game screen), scroll to bottom
    else if (isAuthenticated && !gameState.game && !gameState.loading) {
      gameInitializedRef.current = null; // Reset when no game
      setTimeout(scrollToBottom, 100);
    }
  }, [
    currentGame,
    currentGameState.loading,
    scrollToBottom,
    scrollToRow,
    isAuthenticated,
    gameState.game,
    gameState.loading,
  ]);

  // Auto-scroll on row progression
  useEffect(() => {
    if (currentGame && !currentGameState.loading) {
      const currentRow = currentGame.currentRow;

      // Only scroll if:
      // 1. We have a valid previous row (not the initial -1)
      // 2. Current row is greater than 3 (user has progressed several rows)
      // 3. Current row has actually increased (user climbed up)
      if (previousRowRef.current !== -1 && currentRow > 2 && currentRow > previousRowRef.current) {
        setTimeout(() => scrollToRow(currentRow), 300);
      }

      // Update previous row reference
      previousRowRef.current = currentRow;
    }
  }, [currentGame, currentGame?.currentRow, currentGameState.loading, scrollToRow]);

  // Update balance periodically
  useEffect(() => {
    if (walletState.address) {
      const interval = setInterval(updateBalance, 10000); // Update every 10 seconds
      return () => clearInterval(interval);
    }
  }, [walletState.address, updateBalance]);

  // Show result panel when game ends (won, lost, or cashed out)
  useEffect(() => {
    if (currentGame) {
      const gameEnded = ["won", "lost", "cashing_out", "cashed_out"].includes(currentGame.status);
      setShowResultPanel(gameEnded);

      // Fetch latest reward ratio when game ends and player lost (for DXF rewards)
      if (currentGame.status === "lost" && !isDemoMode && blockchainService.isConnected()) {
        fetchCurrentRewardRatio();
        setTimeout(updateBalance, 3000);
      }
    } else {
      setShowResultPanel(false);
    }
  }, [currentGame, currentGame?.status, isDemoMode, fetchCurrentRewardRatio, updateBalance]);

  // Track row progression and show earnings popup
  useEffect(() => {
    if (!currentGame || priceLoading) return;

    const currentRow = currentGame.currentRow;
    const previousRow = currentRow - 1;

    if (
      currentGame.utilities?.rowSkip?.usedOnRow === previousRow ||
      currentGame.utilities?.phoenixRevival?.usedOnRow === previousRow
    ) {
      return;
    }

    // Show earnings popup when player advances to next row (successful move)
    if (currentRow > 0 && currentGame.status === "active") {
      const bet = ethers.formatEther(BigInt(currentGame.betAmount || "0"));
      const currentMultiplier = calculateMultiplier(
        currentRow,
        currentGame.rowConfigs,
        currentGame.utilities?.rowSkip?.usedOnRow,
        currentGame.utilities?.phoenixRevival?.usedOnRow
      );

      const currentPayoutUSD = calculatePotentialPayoutUSD(
        bet,
        currentRow,
        currentGame.rowConfigs,
        bnbPrice,
        currentGame.utilities?.rowSkip?.usedOnRow,
        currentGame.utilities?.phoenixRevival?.usedOnRow
      );
      const previousPayoutUSD = calculatePotentialPayoutUSD(
        bet,
        previousRow,
        currentGame.rowConfigs,
        bnbPrice,
        currentGame.utilities?.rowSkip?.usedOnRow,
        currentGame.utilities?.phoenixRevival?.usedOnRow
      );
      const betUSD = calculatePotentialPayoutUSD(
        bet,
        0,
        currentGame.rowConfigs,
        bnbPrice,
        currentGame.utilities?.rowSkip?.usedOnRow,
        currentGame.utilities?.phoenixRevival?.usedOnRow
      );
      const rowEarningsUSD = currentPayoutUSD - previousPayoutUSD;
      const totalEarningsUSD = currentPayoutUSD - betUSD;

      setCompletedRow(previousRow);

      // Clear completed row after animation
      setTimeout(() => {
        setCompletedRow(null);
      }, 1000);

      setEarningsData({
        usdAmount: rowEarningsUSD,
        bnbAmount:
          parseFloat(bet) *
            calculateMultiplier(
              currentRow,
              currentGame.rowConfigs,
              currentGame.utilities?.rowSkip?.usedOnRow,
              currentGame.utilities?.phoenixRevival?.usedOnRow
            ) -
          parseFloat(bet) *
            calculateMultiplier(
              previousRow,
              currentGame.rowConfigs,
              currentGame.utilities?.rowSkip?.usedOnRow,
              currentGame.utilities?.phoenixRevival?.usedOnRow
            ),
        currentRow: currentRow,
        totalRows: currentGame.rowConfigs.length,
        multiplier: currentMultiplier,
        totalEarnings: totalEarningsUSD,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGame, currentGame?.currentRow, priceLoading, bnbPrice]);

  // Check if we should show streak reminder modal
  useEffect(() => {
    if (
      isAuthenticated && // Only for authenticated users
      userStreak && // When userStreak data is available
      !isInitializing // When not initializing auth
    ) {
      const { currentStreak, lastGameDate } = userStreak;
      
      if (shouldShowStreakReminder(currentStreak, lastGameDate)) {
        setShowStreakReminderModal(true);
        setReminderTime(); // Record the reminder time to prevent showing again within 1 hour
      }
    }
  }, [isAuthenticated, userStreak, isInitializing]);

  // Check if we should show recovery reminder modal
  useEffect(() => {
    if (
      isAuthenticated && // Only for authenticated users
      userStreak && // When userStreak data is available
      !isInitializing // When not initializing auth
    ) {
      const { eligibleToRecover } = userStreak;
      
      if (shouldShowRecoveryReminderModal(eligibleToRecover)) {
        setShowRecoveryModal(true);
        setRecoveryReminderTime(); // Record the reminder time to prevent showing again within 1 hour
      }
    }
  }, [isAuthenticated, userStreak, isInitializing]);

  // Handler functions for streak reminder modal
  const handleCloseStreakReminder = () => {
    setShowStreakReminderModal(false);
  };

  const handlePlayNow = () => {
    setShowStreakReminderModal(false);
    // On mobile, switch to play tab to ensure game creation popup is visible
    if (mobileTab === "leaderboard") {
      setMobileTab("play");
    }
    setShowGameCreationPopup(true);
  };

  // Handler functions for recovery reminder modal
  const handleCloseRecoveryModal = () => {
    setShowRecoveryModal(false);
  };

  const handleRecoverStreak = async () => {
    if (!userStreak?.recoverStreakPayment) {
      showError("Recovery Failed", "Recovery payment amount not available");
      return;
    }

    try {
      // Send BNB to contract and wait for confirmation
      const txHash = await blockchainService.sendBNBToContract(userStreak.recoverStreakPayment);
      
      // Submit transaction hash to backend for verification and recovery
      const result = await ApiService.recoverStreak(txHash);
      
      showSuccess("Recovery Success", `Streak recovery successful! Recovered ${result.recoveredStreak} streak. Remember to play daily to maintain your streak!`);
      setShowRecoveryModal(false);
      
      // The refresh leaderboard data
      refetchLeaderBoard();
    } catch (error: any) {
      console.error('Recovery failed:', error);
      showError("Recovery Failed", error.message || 'Failed to recover streak');
    }
  };

  // Reset result panel when starting new games
  const handleNewGame = useCallback(async () => {
    setShowResultPanel(false);
    setIsResultPanelCollapsed(false);
    // Reset previous row reference for auto-scroll
    previousRowRef.current = -1;
    // Reset game initialization tracking
    gameInitializedRef.current = null;
    if (isDemoMode) {
      // Directly start a new demo game instead of just resetting
      await startDemoGame(DEFAULT_ROW_CONFIGS);
      setTimeout(scrollToBottom, 100);
    } else {
      resetGame();
    }
  }, [isDemoMode, startDemoGame, resetGame, scrollToBottom]);

  // Handle opening verifier with current game data in new tab
  const handleVerifyCurrentGame = useCallback(() => {
    if (!currentGame) return;

    const gameConfig = currentGame.rowConfigs.map((config) => config.tiles).join(",");
    const seed = currentGame.seed || "";
    const seedHash = currentGame.gameSeedHash || "";

    // Create URL parameters for the new tab
    const params = new URLSearchParams({
      gameConfig,
      seed,
      seedHash,
    });

    // Open verifier in new tab with pre-filled data
    const verifierUrl = `${window.location.origin}/verifier?${params.toString()}`;
    window.open(verifierUrl, "_blank");
  }, [currentGame]);

  // Get result details for display
  const getResultDetails = () => {
    if (!currentGame) return null;

    const multiplier = Math.floor(calculateCurrentMultiplier() * 1000) / 1000;
    const bet = ethers.formatEther(BigInt(currentGame.betAmount || "0"));
    const payout = isDemoMode ? 1 * multiplier : Math.floor(parseFloat(bet) * multiplier * 100000) / 100000;
    const rowsCompleted = currentGame.currentRow;
    const totalRows = currentGame.rowConfigs.length;

    switch (currentGame.status) {
      case "won":
      case "cashing_out":
        return currentGame.currentRow >= totalRows
          ? {
              type: "success",
              title: "CONGRATULATIONS!",
              subtitle: "You completed all rows!",
              bet,
              payout: payout,
              rowsCompleted,
              totalRows,
              icon: "🏆",
            }
          : {
              type: "success",
              title: "CASHING OUT",
              subtitle: "Cashing out your winnings!",
              bet,
              payout: payout,
              rowsCompleted,
              totalRows,
              icon: "💰",
            };
      case "lost":
        // Calculate DXF reward based on potential payout for completed rows
        const potentialPayout = calculatePotentialPayout(
          bet,
          rowsCompleted,
          currentGame.rowConfigs,
          currentGame.utilities?.rowSkip?.usedOnRow,
          currentGame.utilities?.phoenixRevival?.usedOnRow
        );
        // Calculate base DXF reward using contract's current reward ratio
        const rewardRatio = currentRewardRatio || "10000"; // Fallback to default
        const potentialPayoutBN = ethers.parseEther(potentialPayout);
        const ratioWei = ethers.parseEther(rewardRatio);
        let rewardAmountWei = (potentialPayoutBN * ratioWei) / ethers.parseEther("1");

        // Apply DXF Mining Boost if purchased
        const boostInfo = getDXFMiningBoostInfo();
        if (boostInfo) {
          // Apply boost: multiply by boost multiplier
          rewardAmountWei = (rewardAmountWei * BigInt(Math.floor(boostInfo.multiplier * 100))) / BigInt(100);
        }

        const dxfReward = ethers.formatEther(rewardAmountWei);

        return {
          type: "fail",
          title: "GAME OVER",
          subtitle: boostInfo
            ? `You earned boosted DXF tokens! (+${boostInfo.percentage}% bonus)`
            : "You earned DXF tokens!",
          bet,
          payout: parseFloat(dxfReward), // DXF reward amount
          payoutToken: "DXF",
          rowsCompleted,
          totalRows,
          icon: <img src="/icon.png" alt="DeathX Fun" className={gameBoardStyles.resultPanelIcon} />, // DeathX Fun icon for DXF rewards
          boostInfo, // Include boost info for display
        };
      case "cashed_out":
        return {
          type: "success",
          title: "CASHED OUT",
          subtitle: "You successfully withdrew your winnings!",
          bet,
          payout: payout,
          rowsCompleted,
          totalRows,
          icon: "💰",
        };
      default:
        return null;
    }
  };

  // Handle showing verifier
  if (showVerifier) {
    return (
      <div className="App verifier-mode">
        <div className="verifier-back-section">
          <button
            onClick={() => {
              setShowVerifier(false);
              setVerifierParams({});
              navigate("/", { replace: true });
            }}
            className="btn btn-secondary btn-small"
          >
            ← Back to Game
          </button>
        </div>
        <Verifier
          initialGameConfig={verifierParams.gameConfig}
          initialSeed={verifierParams.seed}
          initialSeedHash={verifierParams.seedHash}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <TopNavigation />
      <div className={gameBoardStyles.mainContainer}>
        <div className={gameBoardStyles.gameContentWrapper}>
          {/* Desktop: Left sidebar for Daily Free Draw */}
          {/* <div className="left-sidebar desktop-only"></div> */}

          {/* Main game content */}
          <div
            className={`${gameBoardStyles.gameContent} ${
              mobileTab === "leaderboard" ? gameBoardStyles.mobileHidden : ""
            }`}
          >
            <FloatingDrawButton hasActiveGame={!!currentGame} />
            <WelcomeAirdropNotification hasActiveGame={!!currentGame} />
            {!isAuthenticated && !isDemoMode ? (
              <div className="auth-required">
                <h2>Welcome to DeathX Fun!</h2>
                <div className="welcome-info">
                  <p>
                    <button onClick={() => setShowHowToPlayPopup(true)} className="how-to-play-link" type="button">
                      How to Play
                    </button>
                  </p>
                </div>
                <div className={gameBoardStyles.gameModeSelection}>
                  <div className={gameBoardStyles.modeOption}>
                    <button
                      onClick={async () => {
                        try {
                          await login();
                        } catch (err) {
                          console.error("Login failed:", err);
                        }
                      }}
                      disabled={isInitializing}
                      className="btn btn-primary"
                    >
                      {isInitializing ? "Connecting..." : "💰 Sign In"}
                    </button>
                  </div>
                  <div className={gameBoardStyles.modeDivider}>OR</div>
                  <div className={gameBoardStyles.modeOption}>
                    <button onClick={handleStartDemoGame} className="btn btn-demo">
                      🎮 Play Demo
                    </button>
                  </div>
                </div>
              </div>
            ) : isAuthenticated && !currentGameState.game ? (
              <div className={gameBoardStyles.gameActive}>
                <div className={gameBoardStyles.gameScrollableArea} ref={gameScrollRef}>
                  <div className={gameBoardStyles.gameBoard}>
                    {(currentGame?.rowConfigs || DEFAULT_ROW_CONFIGS).map((rowConfig, rowIndex) => (
                      <div
                        key={rowIndex}
                        className={`${gameBoardStyles.gameRow} ${completedRow === rowIndex ? "completed-row" : ""}`}
                      >
                        <div className={gameBoardStyles.rowLabel}>
                          <span className={gameBoardStyles.rowLabelDesktop}>Row {rowIndex + 1}</span>
                          <span className={gameBoardStyles.rowLabelMobile}>{rowIndex + 1}</span>
                        </div>
                        <div className={gameBoardStyles.rowMultiplierContainer}>
                          <div className={gameBoardStyles.rowMultiplier}>{getRowMultiplierDisplay(rowIndex)}</div>
                          {getRowUSDPreview(rowIndex) && (
                            <div className={gameBoardStyles.rowUsdPreview}>{getRowUSDPreview(rowIndex)}</div>
                          )}
                        </div>
                        <div className={gameBoardStyles.tiles}>
                          {Array.from({ length: rowConfig.tiles }, (_, tileIndex) => (
                            <button
                              key={tileIndex}
                              className={`${gameBoardStyles.tile} hidden`}
                              disabled={true}
                            ></button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={gameBoardStyles.gameStatusFixed}>
                  <div className={gameBoardStyles.gameStatusContent}>
                    <div className={gameBoardStyles.gameStatsHorizontal}>
                      <div>BNB: {walletState.balance ? parseFloat(walletState.balance).toFixed(4) : "0.00"}</div>
                      <div>
                        <a
                          href="https://deathx-fun.notion.site/DeathX-Fun-Token-24e1b048608b81168d99dc0e802cdb93"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="dxf-balance-link"
                        >
                          DXF: {walletState.dxfBalance ? formatDXFBalance(walletState.dxfBalance) : "0"}
                        </a>
                      </div>
                      {/* <div className="game-tips">
                    <span className="tips-trigger">
                      ❓ Tips
                      <div className="tips-content">
                        <h4>How to Play:</h4>
                        <ul>
                          <li>Navigate through rows of tiles</li>
                          <li>Each row has a hidden death tile</li>
                          <li>Choose the safe tile to advance</li>
                          <li>Cash out anytime to secure your winnings</li>
                          <li>Hit a death tile and lose your bet</li>
                        </ul>
                      </div>
                    </span>
                  </div> */}
                    </div>

                    <div className={gameBoardStyles.gameActionsHorizontal}>
                      <div className={gameBoardStyles.betControlsInline}>
                        <button
                          onClick={handleOpenGameCreation}
                          disabled={isInitializing || !isAuthenticated || !walletState.address || gameState.loading}
                          className="btn btn-primary"
                        >
                          {isInitializing ? "Connecting..." : gameState.loading ? "Loading..." : "Start Game"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              currentGame && (
                <div className={gameBoardStyles.gameActive}>
                  {isDemoMode && (
                    <div className={gameBoardStyles.demoBanner}>
                      <span>🎮 DEMO MODE</span>
                      <button onClick={handleBackToRealGame} className="btn btn-secondary btn-small">
                        Exit Demo
                      </button>
                    </div>
                  )}

                  <div className={gameBoardStyles.gameScrollableArea} ref={gameScrollRef}>
                    <div className={gameBoardStyles.gameBoard}>
                      {currentGame.rowConfigs.map((rowConfig, rowIndex) => (
                        <div
                          key={rowIndex}
                          className={`${gameBoardStyles.gameRow} ${completedRow === rowIndex ? "completed-row" : ""} ${
                            currentGame.utilities?.phoenixRevival?.usedOnRow === rowIndex
                              ? gameBoardStyles.revivedRow
                              : ""
                          }`}
                        >
                          <div className={gameBoardStyles.rowLabel}>
                            <span className={gameBoardStyles.rowLabelDesktop}>Row {rowIndex + 1}</span>
                            <span className={gameBoardStyles.rowLabelMobile}>{rowIndex + 1}</span>
                          </div>
                          {currentGame.utilities?.rowSkip?.usedOnRow !== rowIndex &&
                            (currentGame.utilities?.phoenixRevival?.usedOnRow === rowIndex ? (
                              <div className={gameBoardStyles.rowMultiplierContainer}>
                                <div className={gameBoardStyles.phoenixRevivalTag}>🔥 Phoenix</div>
                              </div>
                            ) : (
                              <div className={gameBoardStyles.rowMultiplierContainer}>
                                <div
                                  className={gameBoardStyles.rowMultiplier}
                                  style={{
                                    visibility:
                                      currentGame.utilities?.phoenixRevival?.usedOnRow === undefined ||
                                      rowIndex > currentGame.utilities.phoenixRevival.usedOnRow
                                        ? "visible"
                                        : "hidden",
                                  }}
                                >
                                  {getRowMultiplierDisplay(rowIndex)}
                                </div>
                                {getRowUSDPreview(rowIndex) &&
                                  (currentGame.utilities?.phoenixRevival?.usedOnRow === undefined ||
                                    rowIndex > currentGame.utilities.phoenixRevival.usedOnRow) && (
                                    <div className={gameBoardStyles.rowUsdPreview}>{getRowUSDPreview(rowIndex)}</div>
                                  )}
                              </div>
                            ))}
                          <div className={gameBoardStyles.tiles}>
                            {currentGame.utilities?.rowSkip?.usedOnRow === rowIndex ? (
                              // Show skipped row message
                              <div className={gameBoardStyles.rowSkippedMessage}>⚡ Row Skipped</div>
                            ) : (
                              Array.from({ length: rowConfig.tiles }, (_, tileIndex) => (
                                <button
                                  key={tileIndex}
                                  className={`${gameBoardStyles.tile} ${getTileStatusClasses(
                                    getTileStatus(rowIndex, tileIndex)
                                  )}`}
                                  onClick={() => handleTileClick(rowIndex, tileIndex)}
                                  disabled={
                                    currentGameState.loading ||
                                    currentGame!.status !== "active" ||
                                    rowIndex !== currentGame.currentRow ||
                                    currentGame!.moves.some((m) => m.row === rowIndex && m.tileIndex === tileIndex)
                                  }
                                >
                                  {getTileStatus(rowIndex, tileIndex) === "death" && "💀"}
                                  {getTileStatus(rowIndex, tileIndex) === "revived-death" && "💀"}
                                  {/* {getTileStatus(rowIndex, tileIndex) === "safe" && "✅"} */}
                                  {getTileStatus(rowIndex, tileIndex) === "revealed-death" && "💀"}
                                </button>
                              ))
                            )}
                            {/* Row Skip Button */}
                            {canUseRowSkip() && rowIndex === currentGame.currentRow && (
                              <button
                                onClick={handleRowSkip}
                                disabled={currentGameState.loading}
                                className={gameBoardStyles.rowSkipBtn}
                                title="Skip this dangerous row (loses multiplier)"
                              >
                                {
                                  isMobile ? "Skip" : "⚡ Skip"
                                }
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div
                    className={`${gameBoardStyles.gameStatusFixed} ${showResultPanel ? gameBoardStyles.expanded : ""}`}
                  >
                    <div className={gameBoardStyles.gameStatusContent}>
                      {showResultPanel ? (
                        // Expanded Result Panel
                        <div className={gameBoardStyles.gameResultPanel}>
                          {(() => {
                            const result = getResultDetails();
                            if (!result) return null;

                            return (
                              <>
                                <div className={`${gameBoardStyles.resultHeader} ${gameBoardStyles[result.type]}`}>
                                  <div className={gameBoardStyles.resultIcon}>{result.icon}</div>
                                  <div className={gameBoardStyles.resultText}>
                                    <h2>{result.title}</h2>
                                    <p>{result.subtitle}</p>
                                  </div>
                                  <button
                                    className={gameBoardStyles.resultCollapseBtn}
                                    onClick={() => setIsResultPanelCollapsed(!isResultPanelCollapsed)}
                                    title={isResultPanelCollapsed ? "Show details" : "Hide details"}
                                  >
                                    {isResultPanelCollapsed ? "▲" : "▼"}
                                  </button>
                                </div>

                                <div
                                  className={gameBoardStyles.resultStats}
                                  style={{
                                    maxHeight: isResultPanelCollapsed ? "0" : "500px",
                                    overflow: "hidden",
                                    transition:
                                      "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                    opacity: isResultPanelCollapsed ? 0 : 1,
                                    marginTop: isResultPanelCollapsed ? "0" : "30px",
                                    marginBottom: isResultPanelCollapsed ? "0" : "30px",
                                  }}
                                >
                                  <div className={gameBoardStyles.resultStat}>
                                    <span className={gameBoardStyles.statLabel}>Rows Completed</span>
                                    <span className={gameBoardStyles.statValue}>
                                      {result.rowsCompleted}/{result.totalRows}
                                    </span>
                                  </div>
                                  <div className={gameBoardStyles.resultStat}>
                                    <span className={gameBoardStyles.statLabel}>Final Multiplier</span>
                                    <span className={gameBoardStyles.statValue}>
                                      {formatMultiplier(calculateCurrentMultiplier())}
                                    </span>
                                  </div>
                                  <div className={gameBoardStyles.resultStat}>
                                    <span className={gameBoardStyles.statLabel}>
                                      {"Payout" + (isDemoMode ? " (Demo)" : "")}
                                      {result.boostInfo && (
                                        <span className={gameBoardStyles.boostStat}>
                                          {" +"}
                                          {result.boostInfo.percentage}%
                                        </span>
                                      )}
                                    </span>
                                    <span
                                      className={`${gameBoardStyles.statValue} ${
                                        result.type === "fail" ? gameBoardStyles.lost : gameBoardStyles.won
                                      }`}
                                    >
                                      {`${result.payout} ${result.payoutToken ?? "BNB"}`}
                                    </span>
                                  </div>
                                </div>

                                {/* Game Hash Display in Result Panel */}
                                {currentGame.gameSeedHash && (
                                  <div
                                    className={gameBoardStyles.resultHashSection}
                                    style={{
                                      maxHeight: isResultPanelCollapsed ? "0" : "200px",
                                      overflow: isResultPanelCollapsed ? "hidden" : "visible",
                                      transition:
                                        "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                      opacity: isResultPanelCollapsed ? 0 : 1,
                                      marginTop: isResultPanelCollapsed ? "0" : "25px",
                                      marginBottom: isResultPanelCollapsed ? "0" : "0",
                                    }}
                                  >
                                    <GameHashDisplay
                                      gameSeedHash={currentGame.gameSeedHash}
                                      gameSeed={currentGame.seed}
                                      isGameCompleted={true}
                                    />
                                  </div>
                                )}

                                <div
                                  className={gameBoardStyles.resultActions}
                                  style={{
                                    maxHeight: isResultPanelCollapsed
                                      ? "0"
                                      : window.innerWidth <= 768
                                      ? "300px"
                                      : "150px",
                                    overflow: "hidden",
                                    transition:
                                      "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                    opacity: isResultPanelCollapsed ? 0 : 1,
                                    marginTop: isResultPanelCollapsed ? "0" : "30px",
                                    marginBottom: isResultPanelCollapsed
                                      ? "0"
                                      : window.innerWidth <= 768
                                      ? "16px"
                                      : "0",
                                  }}
                                >
                                  {!isDemoMode && ["won", "cashing_out"].includes(currentGame.status) && calculateCurrentMultiplier() > 1.0 ? (
                                    <button
                                      onClick={handleCashOut}
                                      className="btn btn-primary"
                                      disabled={currentGameState.loading}
                                    >
                                      💰 {currentGameState.loading ? "Cashing Out..." : "Cash Out"}
                                    </button>
                                  ) : (
                                    <button onClick={handleNewGame} className="btn btn-primary">
                                      🎮 Play Again
                                    </button>
                                  )}

                                  {/* Show verify button for completed games (won, lost, cashed out) that have seed data */}
                                  {currentGame.seed && currentGame.gameSeedHash && (
                                    <button onClick={handleVerifyCurrentGame} className="btn btn-primary">
                                      🔍 Verify Game
                                    </button>
                                  )}

                                  {/* Show share button for real games only */}
                                  {!isDemoMode && result && (
                                    <ShareButton
                                      gameResult={{
                                        type: result.type as "success" | "fail",
                                        title: result.title,
                                        bet: result.bet,
                                        payout: result.payout,
                                        payoutToken: result.payoutToken,
                                        rowsCompleted: result.rowsCompleted,
                                        totalRows: result.totalRows,
                                        multiplier: calculateCurrentMultiplier(),
                                      }}
                                    />
                                  )}

                                  {isDemoMode && (
                                    <button onClick={handlePlayForReal} className="btn btn-success">
                                      💰 Play for Real
                                    </button>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ) : (
                        // Normal Status Panel
                        <>
                          <div className={gameBoardStyles.gameStatsHorizontal}>
                            <div className={gameBoardStyles.statItem}>
                              <span className={gameBoardStyles.statLabelDesktop}>Multiplier: </span>
                              <span className={gameBoardStyles.statValue}>
                                {formatMultiplier(calculateCurrentMultiplier())}
                              </span>
                            </div>
                            <div className={gameBoardStyles.statItem}>
                              <span className={gameBoardStyles.statLabelDesktop}>Payout: </span>
                              <span className={gameBoardStyles.statValue}>{formatPotentialPayout()}</span>
                            </div>
                            {getDXFMiningBoostInfo() && (
                              <div
                                className={`${gameBoardStyles.statItem} ${gameBoardStyles.dxfBoostIndicator}`}
                                title={`DXF Mining Boost: +${
                                  getDXFMiningBoostInfo()!.percentage
                                }% extra DXF rewards when you lose`}
                              >
                                <span className={gameBoardStyles.statLabelDesktop}>DXF Boost: </span>
                                <span className={`${gameBoardStyles.statValue} ${gameBoardStyles.boostValue}`}>
                                  +{getDXFMiningBoostInfo()!.percentage}%
                                </span>
                              </div>
                            )}
                            {currentGame.gameSeedHash && (
                              <GameHashDisplay
                                gameSeedHash={currentGame.gameSeedHash}
                                isGameCompleted={["won", "lost", "cashed_out"].includes(currentGame.status)}
                              />
                            )}
                          </div>

                          <div className={gameBoardStyles.gameActionsHorizontal}>
                            {((["active", "cashing_out"].includes(currentGame.status) && currentGame.currentRow > 0 && calculateCurrentMultiplier() > 1.0) ||
                              currentGame.status === "won") && (
                              <button
                                onClick={handleCashOut}
                                disabled={currentGameState.loading}
                                className={`btn btn-success ${gameBoardStyles.cashOutBtn}`}
                              >
                                {currentGameState.loading ? "Processing..." : `💰 Cash Out`}
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            )}

            {/* {gameState.error && <div className="error-message">Error: {gameState.error}</div>} */}
          </div>

          {/* Desktop: Right sidebar for Leaderboard */}
          <div className={`${gameBoardStyles.rightSidebar} ${gameBoardStyles.desktopOnly}`}>
            <WeeklyLeaderboard isActive={true} onRecoverStreak={() => setShowRecoveryModal(true)} />
          </div>
        </div>

        {/* Mobile: Leaderboard content (shown when leaderboard tab is active) */}
        <div
          className={`${gameBoardStyles.mobileLeaderboard} ${mobileTab === "play" ? gameBoardStyles.mobileHidden : ""}`}
        >
          <WeeklyLeaderboard isActive={mobileTab === "leaderboard"} onRecoverStreak={() => setShowRecoveryModal(true)} />
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <BottomNavigation activeTab={mobileTab} onTabChange={setMobileTab} />

      {/* Alert Popup */}
      <AlertPopup alert={alert} onClose={hideAlert} />

      {/* How to Play Popup */}
      <HowToPlayPopup isVisible={showHowToPlayPopup} onClose={() => setShowHowToPlayPopup(false)} />

      {/* Earnings Popup */}
      <EarningsPopup earnings={earningsData} onClose={() => setEarningsData(null)} />

      {/* Cash Out Modal */}
      <CashOutModal
        isOpen={showCashOutModal}
        onClose={() => setShowCashOutModal(false)}
        onJustCashOut={handleJustCashOut}
        onCashOutAndPlayAgain={handleCashOutAndPlayAgain}
        isLoading={currentGameState.loading}
      />

      {/* Event Banner - Hide on mobile when viewing leaderboard */}
      <EventBanner event={currentEvent} onClose={closeCurrentEvent} />

      {/* Game Creation Popup */}
      <GameCreationPopup
        isVisible={showGameCreationPopup}
        onClose={() => setShowGameCreationPopup(false)}
        onCreateGame={handleCreateGameWithUtilities}
        loading={gameState.loading}
      />

      {/* Streak Reminder Modal */}
      <StreakReminderModal
        isOpen={showStreakReminderModal}
        onClose={handleCloseStreakReminder}
        onPlayNow={handlePlayNow}
        currentStreak={userStreak?.currentStreak || 0}
      />

      <RecoverStreakModal
        isOpen={showRecoveryModal}
        onClose={handleCloseRecoveryModal}
        onRecover={handleRecoverStreak}
        lastStreak={userStreak?.lastStreak || 0}
        recoverStreakPayment={userStreak?.recoverStreakPayment || "0.005"}
      />

      {/* Notification Popup */}
      {hasNotifications && (
        <NotificationPopup
          notifications={notifications}
          onClose={removeNotification}
          onAllClosed={clearNotifications}
        />
      )}

      {/* Footer with social links and legal links */}
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-social">
            <a
              href="https://x.com/intent/follow?screen_name=ProofXGames"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              title="Follow us on X"
            >
              𝕏
            </a>
            <a
              href="https://t.me/DXFCommunity"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              title="Join our Telegram"
            >
              Telegram
            </a>
            {/* <a
              href="https://www.youtube.com/channel/UC3OxBhGE_oJTZo0RN9MDWkQ"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              title="Subscribe to our YouTube"
            >
              YouTube
            </a> */}
            <a
              href="https://github.com/deathxfun-game/deathx-fun-smart-contracts"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-social-link"
              title="View our GitHub"
            >
              GitHub
            </a>
            <a href="mailto:info@deathx.fun" className="footer-social-link" title="Contact us via email">
              Email
            </a>
          </div>
          <div className="footer-legal">
            <a
              href="https://deathx-fun.notion.site/Terms-of-Service-24e1b048608b81fc9c24eba29f65900b"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-legal-link"
            >
              Terms of Service
            </a>
            <span className="footer-divider">•</span>
            <a
              href="https://deathx-fun.notion.site/Privacy-Policy-24e1b048608b812ea35ae89acf071640"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-legal-link"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
